/**
 * AIDraftReviewDialog — Phase 5
 *
 * Wraps a draft returned by `/api/ai-copilot/draft`. The dialog renders
 * an "AI-generated draft — review before saving" banner and provides
 * Accept / Edit-then-Accept / Reject controls. Acceptance simply hands
 * the (possibly edited) draft back to the caller via `onAccept(draft)`;
 * persistence is the parent's responsibility. The dialog itself only
 * records audit feedback via POST /api/ai-copilot/feedback.
 *
 * The dialog is intentionally JSON-shaped renderer so it can support all
 * four features (`care_plan_section`, `smart_goal`, `progress_summary`,
 * `support_strategies`) without bespoke UI per feature.
 */
import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, Sparkles, Check, X, Pencil } from 'lucide-react';

function deepClone(o) { return JSON.parse(JSON.stringify(o ?? null)); }

export function AIDraftReviewDialog({
  open, onOpenChange, draftEnvelope, participantId, targetEntity,
  onAccept,
}) {
  const [edited, setEdited] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (draftEnvelope) setEdited(deepClone(draftEnvelope.draft));
    if (!open) setEditMode(false);
  }, [draftEnvelope, open]);

  if (!draftEnvelope) return null;

  const sendFeedback = async (decision) => {
    try {
      await api.post('/ai-copilot/feedback', {
        draft_id: draftEnvelope.draft_id,
        feature: draftEnvelope.feature,
        decision,
        participant_id: participantId,
        target_entity: targetEntity || draftEnvelope.feature,
      });
    } catch { /* non-blocking; backend stays the source of truth */ }
  };

  const reject = async () => {
    setSubmitting(true);
    await sendFeedback('rejected');
    setSubmitting(false);
    onOpenChange(false);
  };
  const accept = async () => {
    setSubmitting(true);
    const decision = editMode ? 'edited_then_accepted' : 'accepted';
    await sendFeedback(decision);
    setSubmitting(false);
    onAccept?.(edited, decision);
    onOpenChange(false);
  };

  const draft = edited || draftEnvelope.draft;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="ai-draft-dialog" className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-amber-500" />
            AI-generated draft
            <Badge variant="outline" className="text-[10px]">{draftEnvelope.feature}</Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900"
             data-testid="ai-draft-banner">
          <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span>
            <strong>AI-generated draft — review before saving.</strong> Nothing has been saved yet.
            Edit, accept, or reject below. {draftEnvelope.model
              ? <span className="opacity-70">({draftEnvelope.provider}:{draftEnvelope.model})</span>
              : null}
          </span>
        </div>

        <div className="max-h-[55vh] overflow-y-auto" data-testid="ai-draft-body">
          {editMode ? (
            <Textarea
              data-testid="ai-draft-editor"
              className="font-mono text-xs h-64"
              value={JSON.stringify(edited, null, 2)}
              onChange={(e) => {
                try { setEdited(JSON.parse(e.target.value)); }
                catch { /* keep typing; we'll re-validate on Accept */ }
              }}
            />
          ) : (
            <DraftRenderer feature={draftEnvelope.feature} data={draft} />
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="ghost" onClick={() => setEditMode((v) => !v)}
            disabled={submitting} data-testid="ai-draft-edit-toggle"
          >
            <Pencil className="h-3.5 w-3.5 mr-1" />
            {editMode ? 'Preview' : 'Edit'}
          </Button>
          <Button
            variant="outline" onClick={reject}
            disabled={submitting} data-testid="ai-draft-reject"
          >
            <X className="h-3.5 w-3.5 mr-1" /> Reject
          </Button>
          <Button
            onClick={accept} disabled={submitting} data-testid="ai-draft-accept"
          >
            <Check className="h-3.5 w-3.5 mr-1" />
            {editMode ? 'Save edits & accept' : 'Accept draft'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DraftRenderer({ feature, data }) {
  if (!data) return <p className="text-xs text-muted-foreground">Empty draft.</p>;
  if (feature === 'smart_goal') {
    return (
      <div className="space-y-3 text-sm">
        <h4 className="font-semibold">{data.title}</h4>
        {['specific', 'measurable', 'achievable', 'relevant', 'time_bound'].map(k => (
          <div key={k}>
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{k.replace('_', ' ')}</p>
            <p>{data[k]}</p>
          </div>
        ))}
        {Array.isArray(data.success_criteria) && (
          <div>
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Success criteria</p>
            <ul className="list-disc list-inside text-sm space-y-0.5">
              {data.success_criteria.map((c, i) => <li key={i}>{c}</li>)}
            </ul>
          </div>
        )}
        {data.suggested_measurement_type && (
          <p className="text-xs">
            <Badge variant="secondary">Measurement: {data.suggested_measurement_type}</Badge>
          </p>
        )}
      </div>
    );
  }
  if (feature === 'care_plan_section') {
    return (
      <div className="space-y-2 text-sm">
        <h4 className="font-semibold">{data.title || data.section_key}</h4>
        {data.summary && <p>{data.summary}</p>}
        {Array.isArray(data.bullets) && (
          <ul className="list-disc list-inside space-y-0.5">
            {data.bullets.map((b, i) => <li key={i}>{b}</li>)}
          </ul>
        )}
      </div>
    );
  }
  if (feature === 'progress_summary') {
    return (
      <div className="space-y-2 text-sm">
        <h4 className="font-semibold">{data.headline}</h4>
        <p className="text-[10px] uppercase tracking-wide text-muted-foreground">period: {data.period_days || '—'} days</p>
        {['key_themes', 'wins', 'concerns', 'recommended_next_steps'].map(k => Array.isArray(data[k]) && (
          <div key={k}>
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{k.replace(/_/g, ' ')}</p>
            <ul className="list-disc list-inside text-sm space-y-0.5">
              {data[k].map((v, i) => <li key={i}>{v}</li>)}
            </ul>
          </div>
        ))}
      </div>
    );
  }
  if (feature === 'support_strategies') {
    return (
      <div className="space-y-3 text-sm">
        {data.goal_title && <h4 className="font-semibold">For: {data.goal_title}</h4>}
        {(data.strategies || []).map((s, i) => (
          <div key={i} className="border rounded p-2">
            <p className="font-medium">{s.title}</p>
            <p className="text-xs text-muted-foreground">{s.description}</p>
            {Array.isArray(s.indicators_of_success) && (
              <ul className="list-disc list-inside text-xs mt-1">
                {s.indicators_of_success.map((iv, j) => <li key={j}>{iv}</li>)}
              </ul>
            )}
          </div>
        ))}
      </div>
    );
  }
  return <pre className="text-xs">{JSON.stringify(data, null, 2)}</pre>;
}
